import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Users, Search, Phone, Mail, Calendar, Briefcase, Clock,
  ChevronRight, MessageSquare, FileText, X, User, Loader2,
  ArrowUpDown, StickyNote, Save, Inbox, CheckCircle2, AlertCircle
} from "lucide-react";

// Time ago helper
const timeAgo = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Format date for detail view
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const styles = {
    Active: { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500" },
    Completed: { bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500" },
    New: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500" },
  };
  
  const style = styles[status] || styles.New;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${style.bg} ${style.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {status}
    </span>
  );
};

// Client Card Component
const ClientCard = ({ client, onClick, isActive }) => {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
        isActive ? "bg-[#1e3a5f]/5 border-l-4 border-l-[#d4a853]" : ""
      }`}
      data-testid={`client-${client.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${
          client.status === "Active" ? "bg-emerald-100 text-emerald-600" : 
          client.status === "Completed" ? "bg-blue-100 text-blue-600" : 
          "bg-slate-100 text-slate-500"
        }`}>
          <User className="w-5 h-5" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold text-[#1e3a5f] truncate">{client.full_name}</span>
            <StatusBadge status={client.status} />
          </div>
          
          {client.last_job_title && (
            <p className="text-sm text-slate-600 truncate mb-1">
              {client.last_job_title}
            </p>
          )}
          
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {timeAgo(client.last_interaction)}
            </span>
            {client.completed_jobs > 0 && (
              <span className="flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                {client.completed_jobs} job{client.completed_jobs > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        
        <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 mt-1" />
      </div>
    </button>
  );
};

// Client Detail Panel
const ClientDetailPanel = ({ client, onClose, onNotesUpdate, isMobile }) => {
  const [notes, setNotes] = useState(client?.notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setNotes(client?.notes || "");
    setHasChanges(false);
  }, [client?.id, client?.notes]);

  const handleSaveNotes = async () => {
    if (!client) return;
    
    setSavingNotes(true);
    try {
      await axios.put(
        `${API}/pro/clients/${client.id}/notes`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Notes saved");
      setHasChanges(false);
      onNotesUpdate?.(client.id, notes);
    } catch (error) {
      toast.error("Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleMessageClick = () => {
    navigate(`/pro/conversations?contact=${client.id}`);
  };

  if (!client) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">Select a client</h3>
          <p className="text-slate-500 max-w-sm">Choose a client from the list to view their details.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="client-detail-panel">
      {/* Header */}
      <div className="p-4 border-b border-slate-100 flex items-center gap-3">
        {isMobile && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg -ml-2"
            data-testid="close-detail-btn"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        )}
        
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
          client.status === "Active" ? "bg-emerald-100 text-emerald-600" : 
          client.status === "Completed" ? "bg-blue-100 text-blue-600" : 
          "bg-slate-100 text-slate-500"
        }`}>
          <User className="w-6 h-6" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-[#1e3a5f] truncate">{client.full_name}</h2>
          <StatusBadge status={client.status} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Contact Info */}
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Contact Info</h3>
          
          <div className="space-y-3">
            {client.phone && (
              <a 
                href={`tel:${client.phone}`}
                className="flex items-center gap-3 text-[#1e3a5f] hover:text-[#d4a853] transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Phone className="w-4 h-4" />
                </div>
                <span className="font-medium">{client.phone}</span>
              </a>
            )}
            
            {client.email && (
              <a 
                href={`mailto:${client.email}`}
                className="flex items-center gap-3 text-[#1e3a5f] hover:text-[#d4a853] transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Mail className="w-4 h-4" />
                </div>
                <span className="font-medium truncate">{client.email}</span>
              </a>
            )}
            
            <div className="flex items-center gap-3 text-slate-600">
              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                <Calendar className="w-4 h-4" />
              </div>
              <span>Client since {formatDate(client.created_at)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 flex gap-2">
            {client.has_conversation !== false && (
              <Button
                onClick={handleMessageClick}
                className="flex-1 bg-[#1e3a5f] hover:bg-[#152a45]"
                data-testid="message-client-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Message
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Summary</h3>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-[#1e3a5f]">{client.completed_jobs || 0}</p>
              <p className="text-xs text-slate-500">Completed Jobs</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-[#1e3a5f]">{client.jobs?.length || 0}</p>
              <p className="text-xs text-slate-500">Total Jobs</p>
            </div>
          </div>
        </div>

        {/* Jobs History */}
        {client.jobs && client.jobs.length > 0 && (
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Jobs History</h3>
            
            <div className="space-y-2">
              {client.jobs.slice(0, 5).map((job) => (
                <Link
                  key={job.id}
                  to={`/job/${job.id}`}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="w-8 h-8 rounded bg-[#1e3a5f]/10 flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-4 h-4 text-[#1e3a5f]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-[#1e3a5f] truncate">
                      {job.title || job.service_category || job.category || "Service"}
                    </p>
                    <p className="text-xs text-slate-400">{formatDate(job.created_at)}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    job.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                    job.status === "open" ? "bg-blue-100 text-blue-700" :
                    "bg-slate-100 text-slate-600"
                  }`}>
                    {job.status}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Private Notes */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2">
              <StickyNote className="w-4 h-4" />
              Private Notes
            </h3>
            {hasChanges && (
              <span className="text-xs text-amber-600">Unsaved changes</span>
            )}
          </div>
          
          <Textarea
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              setHasChanges(true);
            }}
            placeholder="Add private notes about this client..."
            className="min-h-[100px] resize-none border-slate-200"
            data-testid="client-notes-input"
          />
          
          <Button
            onClick={handleSaveNotes}
            disabled={!hasChanges || savingNotes}
            className="mt-3 w-full bg-[#d4a853] hover:bg-[#b8922d] text-[#1e3a5f]"
            data-testid="save-notes-btn"
          >
            {savingNotes ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Notes
          </Button>
        </div>
      </div>
    </div>
  );
};

// Empty State
const EmptyState = () => (
  <div className="h-full flex items-center justify-center p-8">
    <div className="text-center">
      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
        <Inbox className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">No clients yet</h3>
      <p className="text-slate-500 max-w-sm">
        Accept leads and start conversations to build your client list.
      </p>
      <Link to="/pro/leads">
        <Button className="mt-4 bg-[#d4a853] hover:bg-[#b8922d] text-[#1e3a5f]">
          Browse Leads
        </Button>
      </Link>
    </div>
  </div>
);

// Main Component
export default function ProClientsPage() {
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientDetail, setSelectedClientDetail] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showMobileDetail, setShowMobileDetail] = useState(false);

  // Fetch clients
  const fetchClients = useCallback(async () => {
    if (!token) return;
    
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      params.append("sort", sortBy);
      
      const response = await axios.get(`${API}/pro/clients?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients || []);
    } catch (error) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, [token, searchQuery, sortBy]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Fetch client detail
  const fetchClientDetail = useCallback(async (clientId) => {
    if (!token || !clientId) return;
    
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API}/pro/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedClientDetail(response.data);
    } catch (error) {
      toast.error("Failed to load client details");
      setSelectedClientDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [token]);

  // Handle client selection
  const handleSelectClient = (client) => {
    setSelectedClient(client.id);
    fetchClientDetail(client.id);
    setShowMobileDetail(true);
  };

  // Handle notes update
  const handleNotesUpdate = (clientId, notes) => {
    setClients(prev => prev.map(c => 
      c.id === clientId ? { ...c, notes } : c
    ));
    if (selectedClientDetail?.id === clientId) {
      setSelectedClientDetail(prev => ({ ...prev, notes }));
    }
  };

  // Sort options
  const sortOptions = [
    { value: "recent", label: "Most Recent" },
    { value: "alphabetical", label: "Alphabetical" },
    { value: "most_jobs", label: "Most Jobs" },
  ];

  return (
    <div className="h-[calc(100vh-8rem)] flex bg-white rounded-xl shadow-sm overflow-hidden" data-testid="pro-clients-page">
      {/* Client List - Left Panel */}
      <div className={`w-full md:w-80 lg:w-96 border-r border-slate-200 flex flex-col ${
        showMobileDetail && selectedClient ? "hidden md:flex" : "flex"
      }`}>
        {/* Header */}
        <div className="p-4 border-b border-slate-100">
          <h2 className="text-lg font-bold text-[#1e3a5f] mb-3">Clients</h2>
          
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-slate-200"
              data-testid="clients-search"
            />
          </div>
          
          {/* Sort */}
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-4 h-4 text-slate-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#d4a853]/50"
              data-testid="clients-sort"
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Client List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#d4a853] mx-auto" />
            </div>
          ) : clients.length === 0 ? (
            <EmptyState />
          ) : (
            clients.map((client) => (
              <ClientCard
                key={client.id}
                client={client}
                isActive={selectedClient === client.id}
                onClick={() => handleSelectClient(client)}
              />
            ))
          )}
        </div>
        
        {/* Footer with count */}
        {!loading && clients.length > 0 && (
          <div className="p-3 border-t border-slate-100 bg-slate-50">
            <p className="text-xs text-slate-500 text-center">
              {clients.length} client{clients.length > 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>

      {/* Client Detail - Right Panel */}
      <div className={`flex-1 flex flex-col ${
        !showMobileDetail && !selectedClient ? "hidden md:flex" : "flex"
      }`}>
        {detailLoading ? (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#d4a853]" />
          </div>
        ) : (
          <ClientDetailPanel
            client={selectedClientDetail}
            onClose={() => setShowMobileDetail(false)}
            onNotesUpdate={handleNotesUpdate}
            isMobile={showMobileDetail}
          />
        )}
      </div>
    </div>
  );
}
